import React, { useState, useEffect, useCallback } from 'react';
import { getAuditLogs } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollText, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const ACTION_LABELS = {
  login: { label: 'Login', color: '#4A788C' },
  register: { label: 'Registration', color: '#3F6352' },
  submit_hours: { label: 'Submit Hours', color: '#D3735A' },
  review_hours: { label: 'Review Hours', color: '#2C5545' },
  bulk_review: { label: 'Bulk Review', color: '#D4A017' },
  update_user: { label: 'Update User', color: '#4A788C' },
  delete_user: { label: 'Delete User', color: '#B74134' },
  monthly_summary: { label: 'Monthly Summary', color: '#3F6352' },
};

function ActionBadge({ action }) {
  const config = ACTION_LABELS[action] || { label: action, color: '#5C5855' };
  return (
    <span className="px-3 py-1 rounded-full text-xs font-bold border"
      style={{ backgroundColor: `${config.color}15`, color: config.color, borderColor: `${config.color}30` }}
      data-testid={`action-badge-${action}`}>
      {config.label}
    </span>
  );
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (actionFilter !== 'all') params.action = actionFilter;
      const { data } = await getAuditLogs(params);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) { console.error('Failed to load audit logs', e); }
    finally { setLoading(false); }
  }, [actionFilter, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = Math.ceil(total / pageSize);

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  return (
    <div className="animate-fade-in" data-testid="audit-log-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Audit Log</h1>
          <p className="text-[#5C5855] mt-1">Track all system actions for compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-[#5C5855]" />
          <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
            <SelectTrigger data-testid="action-filter" className="w-[180px] border-[#D1C9C0] bg-white">
              <SelectValue placeholder="Filter action" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="login">Logins</SelectItem>
              <SelectItem value="register">Registrations</SelectItem>
              <SelectItem value="submit_hours">Hour Submissions</SelectItem>
              <SelectItem value="review_hours">Hour Reviews</SelectItem>
              <SelectItem value="bulk_review">Bulk Reviews</SelectItem>
              <SelectItem value="update_user">User Updates</SelectItem>
              <SelectItem value="delete_user">User Deletions</SelectItem>
              <SelectItem value="monthly_summary">Monthly Summaries</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-[#5C5855]">{total} total entries</span>
        {totalPages > 1 && (
          <span className="text-sm text-[#8A847F]">| Page {page + 1} of {totalPages}</span>
        )}
      </div>

      <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Timestamp</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Action</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">User</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Role</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i} className="border-b border-[#E8E3DD]">
                  <TableCell colSpan={5} className="px-6 py-4"><div className="h-4 bg-[#F2EFEB] rounded animate-pulse" /></TableCell>
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-[#8A847F]">
                  <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No audit logs found</p>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.audit_id} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors" data-testid={`audit-row-${log.audit_id}`}>
                  <TableCell className="px-6 py-4 text-xs text-[#5C5855] whitespace-nowrap">{formatTimestamp(log.timestamp)}</TableCell>
                  <TableCell className="px-6 py-4"><ActionBadge action={log.action} /></TableCell>
                  <TableCell className="px-6 py-4">
                    <p className="text-sm font-semibold text-[#2D2B2A]">{log.user_name}</p>
                    <p className="text-xs text-[#8A847F] font-mono">{log.user_id === 'system' ? 'System' : log.user_id}</p>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-[#5C5855] capitalize">{log.user_role}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#5C5855] max-w-[300px]">
                    <p className="truncate" title={log.details}>{log.details || '—'}</p>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}
            className="border-[#D1C9C0]" data-testid="prev-page">
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
            className="border-[#D1C9C0]" data-testid="next-page">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
